/**
 * Der Filterzustand der Liste — Lesen und Schreiben im URL, Bezirks-Marke,
 * Adressierung einer Drucksache. Alles pure Funktionen, ohne DOM und ohne
 * Store.
 *
 * Was hier NICHT steht: Suchen, Filtern, Zählen, Sortieren — das ist
 * `entscheidungen-liste.ts`, gegen das Listen-Artefakt im Speicher.
 */

import type { ris } from "@progressive-development/politik-model";

/**
 * Sortierung der Liste. „rang" gibt es nur mit Suchbegriff — Relevanz ohne
 * Anfrage wäre eine Wertung.
 */
export type Sortierung = "neueste" | "aelteste" | "referenz" | "rang";

/**
 * Der Zustand der Seite — alles, was im URL steht.
 *
 * Mehrfachauswahl in jeder Gruppe (ODER in der Gruppe, UND dazwischen). Der
 * Bezirk bleibt einer: Er ist die Klammer, unter der die Stadtteile stehen.
 */
export interface EntscheidungenFilter {
  /** Suchbegriff; leer = keine Suche. */
  q: string;
  /** Bezirks-Slug, trifft `bezirke` (Ortsbezug, Federführung, Stationen). */
  bezirk?: string;
  /** Stadtteil-Slugs, treffen `stadtteil` — die Ortsangabe der Quelle (zweite Stufe unter dem Bezirk). */
  stadtteile: string[];
  instrument: string[];
  /** `urheberArt`. */
  absender: string[];
  /** Partei-Slugs aus `urheberFraktion` — zweite Stufe unter „Fraktion". */
  fraktion: string[];
  /** `zustaendigeEbene`. */
  ebene: string[];
  /** Gremium der letzten Station — Slugs der amtlichen Liste (`gremium`). */
  gremium: string[];
  jahr: number[];
  /** Ergebnisklassen der letzten Station; `OHNE_STATION` für Drucksachen ohne. */
  ergebnis: string[];
  sortierung: Sortierung;
}

export const LEERER_FILTER: EntscheidungenFilter = {
  q: "",
  stadtteile: [],
  instrument: [],
  absender: [],
  fraktion: [],
  ebene: [],
  gremium: [],
  jahr: [],
  ergebnis: [],
  sortierung: "neueste",
};

// =============================================================================
// URL ↔ Filter
// =============================================================================

const SORTIERUNGEN: Sortierung[] = ["neueste", "aelteste", "referenz", "rang"];

/**
 * Die Vorgabe-Sortierung hängt an der Suche: mit Suchbegriff der Rang, sonst
 * das Neueste. Sie steht nicht im URL — nur eine Abweichung davon.
 */
export function standardSortierung(q: string): Sortierung {
  return q.trim() ? "rang" : "neueste";
}

const liste = (wert: string | undefined): string[] => (wert ? wert.split(",").filter(Boolean) : []);

/**
 * Der Filterzustand steht im URL: teilbar, mit funktionierendem
 * Zurück-Button. Unbekannte oder unsinnige Werte werden verworfen, nicht
 * geraten — eine kaputte URL zeigt dann die ungefilterte Liste.
 *
 * `stadtteil` (Einzahl) bleibt als Parameter — die Gebietsseiten verlinken so.
 */
export function filterAusQuery(query: Record<string, string> | undefined): EntscheidungenFilter {
  const q = query ?? {};
  const sortierung = SORTIERUNGEN.find((s) => s === q.sortierung);
  const suchbegriff = q.q ?? "";
  return {
    q: suchbegriff,
    bezirk: q.bezirk || undefined,
    stadtteile: liste(q.stadtteil),
    instrument: liste(q.instrument),
    absender: liste(q.absender),
    fraktion: liste(q.fraktion),
    ebene: liste(q.ebene),
    gremium: liste(q.gremium),
    jahr: liste(q.jahr)
      .map((j) => Number.parseInt(j, 10))
      .filter((j) => Number.isFinite(j)),
    ergebnis: liste(q.ergebnis),
    sortierung: sortierung ?? standardSortierung(suchbegriff),
  };
}

/** Baut den Query-String; Vorgabewerte bleiben weg, damit die URL kurz bleibt. */
export function queryAusFilter(filter: EntscheidungenFilter): string {
  const teile = new URLSearchParams();
  const setze = (name: string, werte: readonly (string | number)[]) => {
    if (werte.length) teile.set(name, werte.join(","));
  };
  if (filter.q.trim()) teile.set("q", filter.q.trim());
  if (filter.bezirk) teile.set("bezirk", filter.bezirk);
  setze("stadtteil", filter.stadtteile);
  setze("instrument", filter.instrument);
  setze("absender", filter.absender);
  setze("fraktion", filter.fraktion);
  setze("ebene", filter.ebene);
  setze("gremium", filter.gremium);
  setze("jahr", filter.jahr);
  setze("ergebnis", filter.ergebnis);
  if (filter.sortierung !== standardSortierung(filter.q)) {
    teile.set("sortierung", filter.sortierung);
  }
  const s = teile.toString();
  return s ? `?${s}` : "";
}

/** Wie viele Filterwerte (ohne Suche und Sortierung) gerade gesetzt sind. */
export function anzahlAktiverFilter(filter: EntscheidungenFilter): number {
  return (
    (filter.bezirk ? 1 : 0) +
    filter.stadtteile.length +
    filter.instrument.length +
    filter.absender.length +
    filter.fraktion.length +
    filter.ebene.length +
    filter.gremium.length +
    filter.jahr.length +
    filter.ergebnis.length
  );
}

/** Einen Wert in einer Mehrfach-Gruppe an- oder abwählen. */
export function umschalten<T>(werte: readonly T[], wert: T, an: boolean): T[] {
  const ohne = werte.filter((w) => w !== wert);
  return an ? [...ohne, wert] : ohne;
}

// =============================================================================
// Suche — Faltung
// =============================================================================

/**
 * Kleinschreibung und Umlaut-Faltung sind hier ausdrücklich erlaubt — das ist
 * KEIN Widerspruch zur Regel „keine Normalisierung". Die gilt für IDENTITÄTEN:
 * Wir raten nie, dass zwei Schreibweisen dasselbe Gebiet meinen. In einem
 * Suchfeld behauptet niemand etwas über die Daten; dort tippt ein Mensch
 * „muelheim" und meint Mülheim.
 */
export function falte(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

// =============================================================================
// Bezirks-Marke
// =============================================================================

export type BezirksMarkenArt = "ort" | "behandelt" | "stadtweit" | "mehrere";

export interface BezirksMarke {
  art: BezirksMarkenArt;
  /** Bezirks-Slug, nur bei „ort" und „behandelt". */
  bezirk?: string;
}

/** Was die Marke braucht — `ris.Paper.derived` oder ein Listeneintrag. */
export interface MarkenQuelle {
  bezirk?: string;
  zustaendigerBezirk?: string;
  zustaendigeEbene?: string;
  bezirke?: string[];
}

/**
 * Die Zeile trägt GENAU EINE Marke, nach dieser Rangfolge — von der genauesten
 * Aussage zur allgemeinsten:
 *
 *   1. `bezirk`             → „Kalk"            (betrifft diesen Ort)
 *   2. `zustaendigerBezirk` → „behandelt in …"
 *   3. Ebene rat/ausschuss  → „Stadtweit"
 *   4. mehrere in `bezirke` → „In mehreren Bezirken"
 *
 * Die Rangfolge gilt in der Liste ausnahmslos, auch bei den wenigen Fällen, in
 * denen Ortsbezug und Behandlung auseinanderfallen. Aufgelöst wird das auf der
 * Detailseite, wo beide Angaben ohnehin benannt stehen und Platz für die
 * Erklärung ist — nicht in einer Zeile.
 */
export function bezirksMarke(quelle: MarkenQuelle): BezirksMarke | undefined {
  if (quelle.bezirk) return { art: "ort", bezirk: quelle.bezirk };
  if (quelle.zustaendigerBezirk) return { art: "behandelt", bezirk: quelle.zustaendigerBezirk };
  const ebene = quelle.zustaendigeEbene;
  if (ebene === "rat" || ebene === "ausschuss") return { art: "stadtweit" };
  if ((quelle.bezirke ?? []).length > 1) return { art: "mehrere" };
  return undefined;
}

/** Dieselbe Marke aus einem `ris.Paper` — die Felder liegen unter `derived`. */
export function bezirksMarkeVonPaper(paper: ris.Paper): BezirksMarke | undefined {
  const d = paper.derived ?? {};
  return bezirksMarke({
    bezirk: d.bezirk as string | undefined,
    zustaendigerBezirk: d.zustaendigerBezirk as string | undefined,
    zustaendigeEbene: d.zustaendigeEbene as string | undefined,
    bezirke: d.bezirke as string[] | undefined,
  });
}

// =============================================================================
// Adressierung einer Drucksache
// =============================================================================

/**
 * Das URL-Segment einer Drucksache.
 *
 * Der sprechende Slug steht in `derived.slug` — er ist eine deterministische
 * Ableitung aus der Referenz und gehört damit dorthin, wo alles Abgeleitete
 * steht, nicht daneben.
 *
 * Der Rückfall auf die Referenz im WORTLAUT (nur URL-kodiert) bleibt, solange
 * Bestände unterwegs sind, die den Slug noch nicht führen. Aus `AN/0673/2026`
 * ein `an-0673-2026` zu bauen wäre etwas anderes — ein erfundener Schlüssel,
 * den die App nicht vergeben darf.
 */
export function drucksacheSegment(paper: {
  derived?: Record<string, unknown>;
  reference: string;
}): string {
  const slug = paper.derived?.slug as string | undefined;
  return slug ?? encodeURIComponent(paper.reference);
}

/**
 * Wie ein URL-Segment nachgeschlagen wird: als Slug oder als Referenz.
 *
 * Entschieden wird an der Form, nicht geraten — eine Referenz enthält
 * Schrägstriche, ein Slug nie.
 */
export function segmentAlsAbfrage(segment: string): {
  feld: "derived.slug" | "reference";
  wert: string;
} {
  const entkodiert = decodeURIComponent(segment);
  return entkodiert.includes("/")
    ? { feld: "reference", wert: entkodiert }
    : { feld: "derived.slug", wert: entkodiert };
}
